/**
 * The 25 built-in drones (PLAN.md §3.8), each a fully specified `parseDrone` input.
 *
 * Sample ids refer to apps/web/public/samples/manifest.json, produced by tools/audio-pipeline. Root pitches are
 * pinned in SAMPLE_ROOTS below and checked against the manifest by the tests, so a rebuilt sample that changes
 * root has to be acknowledged here. Freesound-sourced samples are currently the public HQ previews (lossy)
 * under the same ids; when the originals are fetched (FREESOUND_OAUTH_TOKEN) nothing here changes.
 * PRESET_WANTED names the sample each preset was designed around; where a preset uses a stand-in instead, the
 * swap is one edit to the corresponding entry in this file.
 *
 * Gesture vocabulary (shared DroneSchema): radiusTo ∈ brightness|jivari|grainSize|tilt, velocityTo ∈
 * vibrato|buzz|density|growl|none. Table entries that name a more specific target (radius → cutoff, shimmer,
 * tremolo rate, mixture, BP sweep…) map onto the nearest of these, with the macro wiring noted per preset.
 */
import { parseDrone, type Drone, type DroneInput } from '@tonist/shared';

/** rootMidi / rootHz of every sample referenced below, as built. */
export const SAMPLE_ROOTS = {
  'sitar': { rootMidi: 60, rootHz: 262.80994113206395 },   // grain cloud of jonsept's C4 pluck (Freesound 418063), with real notes F2–D#6
  'tanpura-synth': { rootMidi: 49, rootHz: 138.5913 },
  'choir-synth': { rootMidi: 49, rootHz: 138.5913 },
  'bowl-synth': { rootMidi: 49, rootHz: 138.5913 },
  'wind-synth': { rootMidi: 49, rootHz: 138.5913 },
  'didgeridoo-vcsl': { rootMidi: 37, rootHz: 71.11 },
  'pipe-organ-vcsl': { rootMidi: 48, rootHz: 130.43 },
  'wine-glass-vcsl': { rootMidi: 63, rootHz: 315.79 },
  'gong-vcsl': { rootMidi: 49, rootHz: 141.59 },
  'cello-section-vsco': { rootMidi: 48, rootHz: 130.43 },
  'contrabass-vsco': { rootMidi: 37, rootHz: 69.36 },
  'horn-vsco': { rootMidi: 48, rootHz: 131.15 },
  'bagpipe-drone-g2': { rootMidi: 43, rootHz: 98.16 },
  'bagpipe-drone-g3': { rootMidi: 55, rootHz: 196.72 },
  'tibetan-bowl-bsb': { rootMidi: 62, rootHz: 290.91 },
  'tanpura-sankalp': { rootMidi: 49, rootHz: 138.73 },
  'harmonium-donyaquick': { rootMidi: 49, rootHz: 138.73 },
  'shruti-box-iluppai': { rootMidi: 48, rootHz: 131.51 },
  'didgeridoo-sandyrb': { rootMidi: 33, rootHz: 55.0 },
  'didgeridoo-qubodup': { rootMidi: 36, rootHz: 65.48 },
  'singing-bowls-phluidbox': { rootMidi: 55, rootHz: 195.92 },
  'dungchen-rtb45': { rootMidi: 41, rootHz: 85.87 },
  'kargyraa-hjaarlmarch': { rootMidi: 37, rootHz: 68.47 },
  'chant-yunnan-lepolainyann': { rootMidi: 40, rootHz: 82.76 },
  'vibraphone-bow-cmorris035': { rootMidi: 61, rootHz: 279.07 },
  'crotales-bow-cmorris035': { rootMidi: 73, rootHz: 554.37 },
  'wine-glass-spectral9': { rootMidi: 72, rootHz: 521.74 },
  'hurdy-gurdy-missionariojose': { rootMidi: 49, rootHz: 137.93 },
  'ebow-spurioustransients': { rootMidi: 43, rootHz: 98.36 },
  'cymbal-swell-fiatluxx': { rootMidi: 49, rootHz: 138.5913 },
  'waterphone-be-steele': { rootMidi: 49, rootHz: 138.5913 },
  'analog-drone-rutgermuller': { rootMidi: 58, rootHz: 235.29 },
  'soft-wind-cribbler': { rootMidi: 49, rootHz: 138.5913 },
  'ocean-nox-sound': { rootMidi: 49, rootHz: 138.5913 },
} as const;
export type SampleId = keyof typeof SAMPLE_ROOTS;

type SampleLayer = Extract<DroneInput['sources'][number], { kind: 'sample' }>;
type FreezeLayer = Extract<DroneInput['sources'][number], { kind: 'freeze' }>;

/** A looped sample layer at its built root. */
const sample = (sampleId: SampleId, extra: Partial<Omit<SampleLayer, 'kind' | 'sampleId' | 'rootMidi' | 'rootHz'>> = {}): SampleLayer => ({
  kind: 'sample', sampleId, rootMidi: SAMPLE_ROOTS[sampleId].rootMidi, rootHz: SAMPLE_ROOTS[sampleId].rootHz,
  loop: { start: 0, end: 0, mode: 'baked' }, pitchMode: 'multisample', formantLock: false, semitoneOffset: 0, levelDb: 0, ...extra,
});

/** A granular freeze of a sample. */
const freeze = (sampleId: SampleId, grain: Partial<NonNullable<FreezeLayer['grain']>>, extra: Partial<Omit<FreezeLayer, 'kind' | 'sampleId' | 'rootMidi' | 'grain'>> = {}): FreezeLayer => ({
  kind: 'freeze', sampleId, rootMidi: SAMPLE_ROOTS[sampleId].rootMidi, position: 0.5,
  grain: { sizeMs: 150, density: 24, spraySec: 0.3, pitchJitterCents: 0, texture: 0.5, ...grain }, stretch: 50, semitoneOffset: 0, levelDb: 0, ...extra,
});

const OFF = { on: false, levelDb: -60 } as const;
const NO_UNISON = { voices: 1, detuneCents: 0, spread: 0.5, phaseScatter: 1 } as const;
const NO_DELAY = { timeSec: 0.4, feedback: 0, toneHz: 4000, mix: 0, pingPong: true } as const;
const NO_SHIMMER = { mix: 0, interval: 12, feedback: 0.3 } as const;
const NO_LFO = { rateHz: 0.1, depth: 0, shape: 'sine' } as const;

/** Intended sample per preset (what the preset was voiced for). Synth presets have none. */
export const PRESET_WANTED: Record<string, SampleId | null> = {
  'sa': 'tanpura-sankalp',
  'bindu': null,
  'jivari': 'sitar',
  'harmonium': 'harmonium-donyaquick',
  'shruti': 'shruti-box-iluppai',
  'red-earth': 'didgeridoo-sandyrb',
  'circular-breath': 'didgeridoo-qubodup',
  'singing-bowl': 'singing-bowls-phluidbox',
  'kargyraa': 'kargyraa-hjaarlmarch',
  'gyuke-choir': 'chant-yunnan-lepolainyann',
  'long-horn': 'contrabass-vsco',
  'cello-choir': 'cello-section-vsco',
  'abyss': 'contrabass-vsco',
  'glass': 'wine-glass-spectral9',
  'moonstone': 'vibraphone-bow-cmorris035',
  'chien': 'hurdy-gurdy-missionariojose',
  'pipers-ground': 'bagpipe-drone-g2',
  'cathedral': 'pipe-organ-vcsl',
  'ebow-halo': 'ebow-spurioustransients',
  'bowed-bronze': 'cymbal-swell-fiatluxx',
  'dalis-water': 'waterphone-be-steele',
  'analog-nebula': 'analog-drone-rutgermuller',
  'eschers-staircase': null,
  'solar-wind': null,
  'breath-of-the-world': 'soft-wind-cribbler',
};

const inputs: DroneInput[] = [
  // 1 ─────────────────────────────────────────────────────────────────────── Sa (white)
  // wanted: tanpura-sankalp (real tanpura, C#3). The comb is the jivari: keyed to the zone pitch, radius opens it.
  {
    id: 'sa', name: 'Sa', family: 'white',
    description: 'A real tanpura settling into the note, the jivari humming a little brighter under your finger.',
    sources: [sample('tanpura-sankalp')],
    layers: { sub: OFF, fifth: OFF, octaveUp: OFF, unison: { voices: 2, detuneCents: 3, spread: 0.6, phaseScatter: 1 } },
    filter: { type: 'comb', cutoffHz: 138.59, q: 0.8, keyTrack: 1, combFeedback: 0.3, lfo: NO_LFO },
    breath: { rateHz: 0.07, depth: 0.08, shape: 'sine', phasePerVoice: true },
    motion: { driftAmount: 0.08, driftRateHz: 0.03, targets: ['detune'], idleEvolve: true },
    space: { reverb: { size: 0.7, decaySec: 3.5, damping: 0.5, preDelayMs: 20, mix: 0.18 }, delay: NO_DELAY, shimmer: NO_SHIMMER, width: 1 },
    envelope: { attackSec: 0.4, releaseSec: 3, swellCurve: 'exp' },
    harmony: { autoOctave: false, autoFifth: false, ji: true },
    gesture: { velocityTo: 'none', radiusTo: 'jivari', angleBendCents: 45 },
    macros: { brightness: 0.45, motion: 0.2, density: 0.3, space: 0.35, shimmer: 0 },
  },
  // 2 ─────────────────────────────────────────────────────────────────────── Bindu (white)
  // Additive sine, partials 1–6 at 1/n²; sub an octave down at −9 dB. Radius tilts the spectrum (tilt).
  {
    id: 'bindu', name: 'Bindu', family: 'white',
    description: 'The purest thing in the app: six sines fading as 1/n², breathing so slowly you stop noticing it.',
    sources: [{ kind: 'synth', engine: 'additive', partials: [1, 0.25, 0.1111, 0.0625, 0.04, 0.0278], semitoneOffset: 0, levelDb: 0 }],
    layers: { sub: { on: true, levelDb: -9 }, fifth: OFF, octaveUp: OFF, unison: NO_UNISON },
    filter: { type: 'none', cutoffHz: 8000, q: 0.7, keyTrack: 0, combFeedback: 0.3, lfo: NO_LFO },
    breath: { rateHz: 0.05, depth: 0.1, shape: 'perlin', phasePerVoice: true },
    motion: { driftAmount: 0.05, driftRateHz: 0.02, targets: ['pan'], idleEvolve: true },
    space: { reverb: { size: 0.35, decaySec: 6, damping: 0.25, preDelayMs: 10, mix: 0.3 }, delay: NO_DELAY, shimmer: NO_SHIMMER, width: 0.8 },
    envelope: { attackSec: 1.2, releaseSec: 4, swellCurve: 'sCurve' },
    harmony: { autoOctave: false, autoFifth: false, ji: true },
    gesture: { velocityTo: 'none', radiusTo: 'tilt', angleBendCents: 30 },
    macros: { brightness: 0.3, motion: 0.1, density: 0.2, space: 0.4, shimmer: 0 },
  },
  // 3 ─────────────────────────────────────────────────────────────────────── Jivari (yellow)
  // The supplied sitar (steady-state jivari drone, C#3 −20 c; rootHz 137.0 corrects it). Fifth at −10 dB, comb 0.35.
  {
    id: 'jivari', name: 'Jivari', family: 'yellow',
    description: 'The sitar drone with its buzzing bridge, its own fifth ringing inside it and a faint shimmer above.',
    sources: [sample('sitar')],
    // the sample is a grain cloud of a real C4 pluck with the real G4 a fifth above already mixed in (see the audio
    // pipeline's cloud.ts); no transposed fifth layer on top of it
    layers: { sub: OFF, fifth: OFF, octaveUp: OFF, unison: NO_UNISON },
    filter: { type: 'comb', cutoffHz: 138.59, q: 0.8, keyTrack: 1, combFeedback: 0.35, lfo: NO_LFO },
    breath: { rateHz: 0.08, depth: 0.05, shape: 'sine', phasePerVoice: true },
    motion: { driftAmount: 0.1, driftRateHz: 0.04, targets: ['cutoff'], idleEvolve: true },
    space: { reverb: { size: 0.75, decaySec: 4, damping: 0.45, preDelayMs: 25, mix: 0.22 }, delay: NO_DELAY, shimmer: { mix: 0.08, interval: 12, feedback: 0.3 }, width: 1.1 },
    envelope: { attackSec: 0.3, releaseSec: 3, swellCurve: 'exp' },
    harmony: { autoOctave: false, autoFifth: false, ji: true },
    gesture: { velocityTo: 'vibrato', radiusTo: 'jivari', angleBendCents: 45 },
    macros: { brightness: 0.55, motion: 0.25, density: 0.3, space: 0.4, shimmer: 0.08 },
  },
  // 4 ─────────────────────────────────────────────────────────────────────── Harmonium (yellow)
  // donyaquick harmonium, C#3 drone. One bellows for all voices (phasePerVoice false), triangle pumping at 0.5 Hz.
  {
    id: 'harmonium', name: 'Harmonium', family: 'yellow',
    description: 'Reed harmonium with the bellows still pumping, two slightly disagreeing reeds per note.',
    sources: [sample('harmonium-donyaquick')],
    layers: { sub: OFF, fifth: OFF, octaveUp: OFF, unison: { voices: 2, detuneCents: 5, spread: 0.5, phaseScatter: 1 } },
    filter: { type: 'lp', cutoffHz: 6000, q: 0.7, keyTrack: 0.3, combFeedback: 0.3, lfo: NO_LFO },
    breath: { rateHz: 0.5, depth: 0.12, shape: 'tri', phasePerVoice: false },
    motion: { driftAmount: 0.06, driftRateHz: 0.05, targets: ['cutoff'], idleEvolve: true },
    space: { reverb: { size: 0.35, decaySec: 1.8, damping: 0.55, preDelayMs: 12, mix: 0.16 }, delay: NO_DELAY, shimmer: NO_SHIMMER, width: 1 },
    envelope: { attackSec: 0.25, releaseSec: 2.5, swellCurve: 'exp' },
    harmony: { autoOctave: false, autoFifth: false, ji: true },
    gesture: { velocityTo: 'none', radiusTo: 'brightness', angleBendCents: 35 },
    macros: { brightness: 0.5, motion: 0.3, density: 0.4, space: 0.25, shimmer: 0 },
  },
  // 5 ─────────────────────────────────────────────────────────────────────── Shruti (white)
  // iluppai shruti box. The filter is effectively open (5 kHz) and only the slow LFO moves it; cathedral behind.
  {
    id: 'shruti', name: 'Shruti', family: 'white',
    description: 'A shruti box left open in a cold stone church, the reeds slowly darkening and clearing.',
    sources: [sample('shruti-box-iluppai')],
    layers: { sub: OFF, fifth: OFF, octaveUp: OFF, unison: NO_UNISON },
    filter: { type: 'lp', cutoffHz: 5000, q: 0.6, keyTrack: 0, combFeedback: 0.3, lfo: { rateHz: 0.1, depth: 0.3, shape: 'sine' } },
    breath: { rateHz: 0.3, depth: 0.06, shape: 'tri', phasePerVoice: false },
    motion: { driftAmount: 0.05, driftRateHz: 0.03, targets: ['cutoff'], idleEvolve: true },
    space: { reverb: { size: 0.85, decaySec: 5, damping: 0.4, preDelayMs: 40, mix: 0.2 }, delay: NO_DELAY, shimmer: NO_SHIMMER, width: 1 },
    envelope: { attackSec: 0.35, releaseSec: 3, swellCurve: 'exp' },
    harmony: { autoOctave: false, autoFifth: false, ji: true },
    gesture: { velocityTo: 'none', radiusTo: 'brightness', angleBendCents: 35 },
    macros: { brightness: 0.6, motion: 0.2, density: 0.3, space: 0.4, shimmer: 0 },
  },
  // 6 ─────────────────────────────────────────────────────────────────────── Red Earth (red)
  // sandyrb didgeridoo (A1). Sub −6 dB, vowel-morph formant LFO 0.15 Hz, the 3.5 Hz circular-breath pulsation.
  {
    id: 'red-earth', name: 'Red Earth', family: 'red',
    description: 'Didgeridoo with the breath rhythm kept in: a vowel that slowly turns over a pulsing floor.',
    sources: [sample('didgeridoo-sandyrb')],
    layers: { sub: { on: true, levelDb: -6 }, fifth: OFF, octaveUp: OFF, unison: NO_UNISON },
    filter: { type: 'formant', cutoffHz: 600, q: 3, keyTrack: 0, combFeedback: 0.3, lfo: { rateHz: 0.15, depth: 0.5, shape: 'sine' } },
    breath: { rateHz: 3.5, depth: 0.3, shape: 'sine', phasePerVoice: false },
    motion: { driftAmount: 0.15, driftRateHz: 0.05, targets: ['cutoff'], idleEvolve: true },
    space: { reverb: { size: 0.3, decaySec: 1.6, damping: 0.6, preDelayMs: 8, mix: 0.12 }, delay: NO_DELAY, shimmer: NO_SHIMMER, width: 0.9 },
    envelope: { attackSec: 0.2, releaseSec: 2, swellCurve: 'exp' },
    harmony: { autoOctave: false, autoFifth: false, ji: true },
    gesture: { velocityTo: 'buzz', radiusTo: 'brightness', angleBendCents: 45 },
    macros: { brightness: 0.45, motion: 0.4, density: 0.5, space: 0.15, shimmer: 0 },
  },
  // 7 ─────────────────────────────────────────────────────────────────────── Circular Breath (red)
  // Granular freeze of qubodup's 2:15 circular-breathing didgeridoo; velocity drives grain density.
  {
    id: 'circular-breath', name: 'Circular Breath', family: 'red',
    description: 'A didgeridoo frozen mid-breath and scattered into grains that never need to inhale.',
    sources: [freeze('didgeridoo-qubodup', { sizeMs: 120, density: 24, spraySec: 0.3, pitchJitterCents: 0, texture: 0.45 }, { stretch: 40 })],
    layers: { sub: { on: true, levelDb: -8 }, fifth: OFF, octaveUp: OFF, unison: NO_UNISON },
    filter: { type: 'lp', cutoffHz: 3500, q: 0.7, keyTrack: 0.2, combFeedback: 0.3, lfo: { rateHz: 0.07, depth: 0.2, shape: 'perlin' } },
    breath: { rateHz: 0.12, depth: 0.1, shape: 'perlin', phasePerVoice: true },
    motion: { driftAmount: 0.2, driftRateHz: 0.06, targets: ['cutoff', 'pan'], idleEvolve: true },
    space: { reverb: { size: 0.8, decaySec: 4, damping: 0.5, preDelayMs: 30, mix: 0.3 }, delay: NO_DELAY, shimmer: NO_SHIMMER, width: 1.2 },
    envelope: { attackSec: 0.5, releaseSec: 3, swellCurve: 'exp' },
    harmony: { autoOctave: false, autoFifth: false, ji: true },
    gesture: { velocityTo: 'density', radiusTo: 'grainSize', angleBendCents: 45 },
    macros: { brightness: 0.4, motion: 0.35, density: 0.5, space: 0.45, shimmer: 0 },
  },
  // 8 ─────────────────────────────────────────────────────────────────────── Singing Bowl (blue)
  // phluidbox bowls (G3) with a second, smaller bowl underneath; three voices at ±7 c beat like real bowls.
  {
    id: 'singing-bowl', name: 'Singing Bowl', family: 'blue',
    description: 'Two rubbed bowls that swell in over a second and beat gently against each other for as long as you stay.',
    sources: [sample('singing-bowls-phluidbox'), sample('tibetan-bowl-bsb', { levelDb: -10 })],
    layers: { sub: OFF, fifth: OFF, octaveUp: OFF, unison: { voices: 3, detuneCents: 7, spread: 0.7, phaseScatter: 1 } },
    filter: { type: 'hp', cutoffHz: 80, q: 0.7, keyTrack: 0, combFeedback: 0.3, lfo: NO_LFO },
    breath: { rateHz: 0.2, depth: 0.15, shape: 'sine', phasePerVoice: true },
    motion: { driftAmount: 0.1, driftRateHz: 0.03, targets: ['detune'], idleEvolve: true },
    space: { reverb: { size: 0.9, decaySec: 8, damping: 0.35, preDelayMs: 30, mix: 0.35 }, delay: NO_DELAY, shimmer: NO_SHIMMER, width: 1.2 },
    envelope: { attackSec: 1.2, releaseSec: 5, swellCurve: 'sCurve' },
    harmony: { autoOctave: false, autoFifth: false, ji: true },
    gesture: { velocityTo: 'none', radiusTo: 'tilt', angleBendCents: 30 },
    macros: { brightness: 0.5, motion: 0.25, density: 0.4, space: 0.5, shimmer: 0 },
  },
  // 9 ─────────────────────────────────────────────────────────────────────── Kargyraa (red)
  // Hjaarlmarch kargyraa (C#2). Formant-locked stretch so the throat stays a throat across the octaves; sub −3 dB.
  {
    id: 'kargyraa', name: 'Kargyraa', family: 'red',
    description: 'Undertone throat singing with the chest kept in place, the vowel following your finger outward.',
    sources: [sample('kargyraa-hjaarlmarch', { pitchMode: 'stretch', formantLock: true })],
    layers: { sub: { on: true, levelDb: -3 }, fifth: OFF, octaveUp: OFF, unison: NO_UNISON },
    filter: { type: 'formant', cutoffHz: 500, q: 2.5, keyTrack: 0, combFeedback: 0.3, lfo: { rateHz: 0.05, depth: 0.2, shape: 'perlin' } },
    breath: { rateHz: 0.25, depth: 0.08, shape: 'perlin', phasePerVoice: false },
    motion: { driftAmount: 0.3, driftRateHz: 0.06, targets: ['cutoff', 'detune'], idleEvolve: true },
    space: { reverb: { size: 0.25, decaySec: 1.2, damping: 0.6, preDelayMs: 6, mix: 0.1 }, delay: NO_DELAY, shimmer: NO_SHIMMER, width: 0.7 },
    envelope: { attackSec: 0.3, releaseSec: 2, swellCurve: 'exp' },
    harmony: { autoOctave: false, autoFifth: false, ji: true },
    gesture: { velocityTo: 'growl', radiusTo: 'brightness', angleBendCents: 45 },
    macros: { brightness: 0.4, motion: 0.4, density: 0.5, space: 0.1, shimmer: 0 },
  },
  // 10 ────────────────────────────────────────────────────────────────────── Gyuke Choir (white)
  // Freeze of the Yunnan chant (texture) over the synthetic vowel choir (pitch centre), three voices, cathedral.
  {
    id: 'gyuke-choir', name: 'Gyuke Choir', family: 'white',
    description: 'A monastery of low voices, stretched until the words dissolve, with a bass drone under the hall.',
    sources: [
      freeze('chant-yunnan-lepolainyann', { sizeMs: 200, density: 30, spraySec: 0.8, pitchJitterCents: 6, texture: 0.6 }, { stretch: 60 }),
      sample('choir-synth', { levelDb: -12 }),
    ],
    layers: { sub: { on: true, levelDb: -9 }, fifth: OFF, octaveUp: OFF, unison: { voices: 3, detuneCents: 4, spread: 0.8, phaseScatter: 1 } },
    filter: { type: 'lp', cutoffHz: 4500, q: 0.6, keyTrack: 0, combFeedback: 0.3, lfo: { rateHz: 0.04, depth: 0.15, shape: 'perlin' } },
    breath: { rateHz: 0.1, depth: 0.1, shape: 'perlin', phasePerVoice: true },
    motion: { driftAmount: 0.2, driftRateHz: 0.04, targets: ['pan', 'cutoff'], idleEvolve: true },
    space: { reverb: { size: 0.95, decaySec: 7, damping: 0.4, preDelayMs: 45, mix: 0.45 }, delay: NO_DELAY, shimmer: NO_SHIMMER, width: 1.3 },
    envelope: { attackSec: 0.8, releaseSec: 4, swellCurve: 'sCurve' },
    harmony: { autoOctave: false, autoFifth: false, ji: true },
    gesture: { velocityTo: 'density', radiusTo: 'grainSize', angleBendCents: 30 },
    macros: { brightness: 0.4, motion: 0.3, density: 0.55, space: 0.6, shimmer: 0 },
  },
  // 11 ────────────────────────────────────────────────────────────────────── Long Horn (red)
  // Dungchen by construction: contrabass an octave down + horn, plus a freeze of the real Bhutan horns for the rasp.
  // Radius → cutoff maps onto 'brightness'; the fixed 0.15 drive lives on velocity → buzz.
  {
    id: 'long-horn', name: 'Long Horn', family: 'red',
    description: 'A Tibetan long horn built from contrabass and horn, swelling in like brass and rasping when pushed.',
    sources: [
      sample('contrabass-vsco', { semitoneOffset: -12, levelDb: -3 }),
      sample('horn-vsco', { levelDb: -6 }),
      freeze('dungchen-rtb45', { sizeMs: 250, density: 20, spraySec: 0.4, pitchJitterCents: 4, texture: 0.5 }, { levelDb: -12, stretch: 30 }),
    ],
    layers: { sub: OFF, fifth: OFF, octaveUp: OFF, unison: NO_UNISON },
    filter: { type: 'lp', cutoffHz: 1200, q: 0.9, keyTrack: 0.2, combFeedback: 0.3, lfo: { rateHz: 0.1, depth: 0.3, shape: 'sine' } },
    breath: { rateHz: 0.15, depth: 0.1, shape: 'sine', phasePerVoice: false },
    motion: { driftAmount: 0.12, driftRateHz: 0.04, targets: ['cutoff'], idleEvolve: true },
    space: { reverb: { size: 0.85, decaySec: 6, damping: 0.5, preDelayMs: 35, mix: 0.3 }, delay: NO_DELAY, shimmer: NO_SHIMMER, width: 1 },
    envelope: { attackSec: 1.8, releaseSec: 3.5, swellCurve: 'sCurve' },
    harmony: { autoOctave: false, autoFifth: false, ji: true },
    gesture: { velocityTo: 'buzz', radiusTo: 'brightness', angleBendCents: 40 },
    macros: { brightness: 0.35, motion: 0.25, density: 0.5, space: 0.45, shimmer: 0 },
  },
  // 12 ────────────────────────────────────────────────────────────────────── Cello Choir (blue)
  {
    id: 'cello-choir', name: 'Cello Choir', family: 'blue',
    description: 'A cello section spread wide, three bows never quite in tune, in a warm hall.',
    sources: [sample('cello-section-vsco')],
    layers: { sub: OFF, fifth: OFF, octaveUp: OFF, unison: { voices: 3, detuneCents: 6, spread: 0.8, phaseScatter: 1 } },
    filter: { type: 'lp', cutoffHz: 2500, q: 0.7, keyTrack: 0.3, combFeedback: 0.3, lfo: { rateHz: 0.05, depth: 0.25, shape: 'sine' } },
    breath: { rateHz: 0.12, depth: 0.08, shape: 'sine', phasePerVoice: true },
    motion: { driftAmount: 0.1, driftRateHz: 0.04, targets: ['detune', 'cutoff'], idleEvolve: true },
    space: { reverb: { size: 0.75, decaySec: 4, damping: 0.5, preDelayMs: 25, mix: 0.28 }, delay: NO_DELAY, shimmer: NO_SHIMMER, width: 1.2 },
    envelope: { attackSec: 0.6, releaseSec: 3, swellCurve: 'sCurve' },
    harmony: { autoOctave: false, autoFifth: false, ji: true },
    gesture: { velocityTo: 'vibrato', radiusTo: 'brightness', angleBendCents: 40 },
    macros: { brightness: 0.45, motion: 0.3, density: 0.5, space: 0.4, shimmer: 0 },
  },
  // 13 ────────────────────────────────────────────────────────────────────── Abyss (blue)
  // Contrabass two octaves down, sub, 350 Hz low-pass wandering with drift, ten-second reverb.
  {
    id: 'abyss', name: 'Abyss', family: 'blue',
    description: 'A contrabass dropped two octaves into black water, heard through a hole in the floor.',
    sources: [sample('contrabass-vsco', { semitoneOffset: -12, pitchMode: 'resample' })],
    layers: { sub: { on: true, levelDb: -6 }, fifth: OFF, octaveUp: OFF, unison: NO_UNISON },
    filter: { type: 'lp', cutoffHz: 350, q: 1.1, keyTrack: 0.5, combFeedback: 0.3, lfo: { rateHz: 0.03, depth: 0.2, shape: 'perlin' } },
    breath: { rateHz: 0.04, depth: 0.12, shape: 'sine', phasePerVoice: true },
    motion: { driftAmount: 0.4, driftRateHz: 0.03, targets: ['cutoff'], idleEvolve: true },
    space: { reverb: { size: 1, decaySec: 10, damping: 0.7, preDelayMs: 60, mix: 0.4 }, delay: NO_DELAY, shimmer: NO_SHIMMER, width: 1 },
    envelope: { attackSec: 1.5, releaseSec: 6, swellCurve: 'sCurve' },
    harmony: { autoOctave: false, autoFifth: false, ji: true },
    gesture: { velocityTo: 'none', radiusTo: 'brightness', angleBendCents: 30 },
    macros: { brightness: 0.2, motion: 0.4, density: 0.4, space: 0.6, shimmer: 0 },
  },
  // 14 ────────────────────────────────────────────────────────────────────── Glass (white)
  // wanted: wine-glass-spectral9 (C5). The VCSL glass (D#4) carries the note near its own register; spectral9
  // sits an octave up for sparkle. Radius → shimmer is wired through the shimmer macro; radiusTo is brightness.
  {
    id: 'glass', name: 'Glass', family: 'white',
    description: 'Rubbed wine glasses with an octave shimmer that keeps climbing out of the plate.',
    sources: [sample('wine-glass-vcsl'), sample('wine-glass-spectral9', { semitoneOffset: 12, levelDb: -8 })],
    layers: { sub: OFF, fifth: OFF, octaveUp: OFF, unison: { voices: 2, detuneCents: 2, spread: 0.6, phaseScatter: 1 } },
    filter: { type: 'hp', cutoffHz: 120, q: 0.7, keyTrack: 0, combFeedback: 0.3, lfo: NO_LFO },
    breath: { rateHz: 0.15, depth: 0.1, shape: 'sine', phasePerVoice: true },
    motion: { driftAmount: 0.08, driftRateHz: 0.05, targets: ['pan'], idleEvolve: true },
    space: { reverb: { size: 0.4, decaySec: 5, damping: 0.2, preDelayMs: 15, mix: 0.3 }, delay: NO_DELAY, shimmer: { mix: 0.25, interval: 12, feedback: 0.4 }, width: 1.2 },
    envelope: { attackSec: 0.8, releaseSec: 4, swellCurve: 'sCurve' },
    harmony: { autoOctave: false, autoFifth: false, ji: true },
    gesture: { velocityTo: 'none', radiusTo: 'brightness', angleBendCents: 25 },
    macros: { brightness: 0.55, motion: 0.15, density: 0.3, space: 0.45, shimmer: 0.25 },
  },
  // 15 ────────────────────────────────────────────────────────────────────── Moonstone (green)
  // Bowed vibraphone + crotales an octave up at −12 dB; the vibraphone motor is the 5.5 Hz tremolo (shared phase).
  // Radius → tremolo rate maps onto 'tilt' (the closest gesture in the shared schema).
  {
    id: 'moonstone', name: 'Moonstone', family: 'green',
    description: 'Bowed vibraphone with the motor running and bowed crotales glinting an octave above.',
    sources: [sample('vibraphone-bow-cmorris035'), sample('crotales-bow-cmorris035', { semitoneOffset: 12, levelDb: -12 })],
    layers: { sub: OFF, fifth: OFF, octaveUp: OFF, unison: NO_UNISON },
    filter: { type: 'lp', cutoffHz: 7000, q: 0.7, keyTrack: 0, combFeedback: 0.3, lfo: NO_LFO },
    breath: { rateHz: 5.5, depth: 0.2, shape: 'sine', phasePerVoice: false },
    motion: { driftAmount: 0.08, driftRateHz: 0.05, targets: ['pan'], idleEvolve: true },
    space: { reverb: { size: 0.8, decaySec: 5, damping: 0.35, preDelayMs: 25, mix: 0.3 }, delay: NO_DELAY, shimmer: { mix: 0.15, interval: 12, feedback: 0.3 }, width: 1.2 },
    envelope: { attackSec: 0.7, releaseSec: 4, swellCurve: 'sCurve' },
    harmony: { autoOctave: false, autoFifth: false, ji: true },
    gesture: { velocityTo: 'none', radiusTo: 'tilt', angleBendCents: 30 },
    macros: { brightness: 0.55, motion: 0.35, density: 0.3, space: 0.45, shimmer: 0.15 },
  },
  // 16 ────────────────────────────────────────────────────────────────────── Chien (yellow)
  // Hurdy-gurdy (C#3). Fifth + sub, comb, and the chien: a 6 Hz triangle tremolo whose drive follows velocity.
  {
    id: 'chien', name: 'Chien', family: 'yellow',
    description: 'A hurdy-gurdy with the buzzing bridge engaged, barking harder the faster you move.',
    sources: [sample('hurdy-gurdy-missionariojose')],
    layers: { sub: { on: true, levelDb: -9 }, fifth: { on: true, levelDb: -8 }, octaveUp: OFF, unison: NO_UNISON },
    filter: { type: 'comb', cutoffHz: 138.59, q: 0.8, keyTrack: 1, combFeedback: 0.4, lfo: NO_LFO },
    breath: { rateHz: 6, depth: 0.35, shape: 'tri', phasePerVoice: false },
    motion: { driftAmount: 0.1, driftRateHz: 0.08, targets: ['cutoff'], idleEvolve: true },
    space: { reverb: { size: 0.3, decaySec: 1.5, damping: 0.55, preDelayMs: 8, mix: 0.12 }, delay: NO_DELAY, shimmer: NO_SHIMMER, width: 0.9 },
    envelope: { attackSec: 0.15, releaseSec: 2, swellCurve: 'exp' },
    harmony: { autoOctave: false, autoFifth: false, ji: true },
    gesture: { velocityTo: 'buzz', radiusTo: 'brightness', angleBendCents: 45 },
    macros: { brightness: 0.55, motion: 0.45, density: 0.5, space: 0.15, shimmer: 0 },
  },
  // 17 ────────────────────────────────────────────────────────────────────── Piper's Ground (green)
  // FreePats pipe drones: G2 tenor at pitch, G3 bass drone an octave up; octave layers on, two voices at ±8 c
  // (real pipes beat). Suggests the Khamaj / Pipes scale with its 7/4.
  {
    id: 'pipers-ground', name: "Piper's Ground", family: 'green',
    description: 'Bagpipe drones, tenor and bass, beating slowly against each other in a small dry room.',
    sources: [sample('bagpipe-drone-g2'), sample('bagpipe-drone-g3', { semitoneOffset: 12, levelDb: -4 })],
    layers: { sub: { on: true, levelDb: -12 }, fifth: OFF, octaveUp: { on: true, levelDb: -14 }, unison: { voices: 2, detuneCents: 8, spread: 0.5, phaseScatter: 1 } },
    filter: { type: 'lp', cutoffHz: 6500, q: 0.7, keyTrack: 0, combFeedback: 0.3, lfo: NO_LFO },
    breath: { rateHz: 0.2, depth: 0.04, shape: 'tri', phasePerVoice: false },
    motion: { driftAmount: 0.08, driftRateHz: 0.05, targets: ['detune'], idleEvolve: true },
    space: { reverb: { size: 0.3, decaySec: 1.4, damping: 0.5, preDelayMs: 10, mix: 0.12 }, delay: NO_DELAY, shimmer: NO_SHIMMER, width: 1 },
    envelope: { attackSec: 0.5, releaseSec: 2.5, swellCurve: 'exp' },
    harmony: { autoOctave: true, autoFifth: false, ji: true },
    gesture: { velocityTo: 'none', radiusTo: 'brightness', angleBendCents: 30 },
    macros: { brightness: 0.5, motion: 0.2, density: 0.5, space: 0.15, shimmer: 0 },
  },
  // 18 ────────────────────────────────────────────────────────────────────── Cathedral (yellow)
  // VCSL pipe organ: 8' + 4' (+12, −6 dB) + 2⅔' (+19, −12 dB). Radius → mixture maps onto brightness; the
  // tremulant is the 5.2 Hz breath, almost off here and brought up by the Motion macro.
  {
    id: 'cathedral', name: 'Cathedral', family: 'yellow',
    description: 'Pipe organ with the mixture drawn, nine seconds of stone behind it and a tremulant waiting on the Motion knob.',
    sources: [
      sample('pipe-organ-vcsl'),
      sample('pipe-organ-vcsl', { semitoneOffset: 12, levelDb: -6 }),
      sample('pipe-organ-vcsl', { semitoneOffset: 19, levelDb: -12 }),
    ],
    layers: { sub: OFF, fifth: OFF, octaveUp: OFF, unison: NO_UNISON },
    filter: { type: 'lp', cutoffHz: 8000, q: 0.7, keyTrack: 0, combFeedback: 0.3, lfo: NO_LFO },
    breath: { rateHz: 5.2, depth: 0.04, shape: 'sine', phasePerVoice: false },
    motion: { driftAmount: 0.05, driftRateHz: 0.05, targets: ['breath'], idleEvolve: false },
    space: { reverb: { size: 1, decaySec: 9, damping: 0.4, preDelayMs: 50, mix: 0.5 }, delay: NO_DELAY, shimmer: NO_SHIMMER, width: 1.2 },
    envelope: { attackSec: 0.3, releaseSec: 3, swellCurve: 'exp' },
    harmony: { autoOctave: false, autoFifth: false, ji: true },
    gesture: { velocityTo: 'none', radiusTo: 'brightness', angleBendCents: 25 },
    macros: { brightness: 0.6, motion: 0.15, density: 0.6, space: 0.65, shimmer: 0 },
  },
  // 19 ────────────────────────────────────────────────────────────────────── E-Bow Halo (green)
  {
    id: 'ebow-halo', name: 'E-Bow Halo', family: 'green',
    description: 'An e-bowed guitar string bouncing between the speakers, each echo a little softer and higher.',
    sources: [sample('ebow-spurioustransients')],
    layers: { sub: OFF, fifth: OFF, octaveUp: OFF, unison: { voices: 2, detuneCents: 4, spread: 0.7, phaseScatter: 1 } },
    filter: { type: 'lp', cutoffHz: 5000, q: 0.7, keyTrack: 0.2, combFeedback: 0.3, lfo: { rateHz: 0.06, depth: 0.15, shape: 'sine' } },
    breath: { rateHz: 0.1, depth: 0.06, shape: 'sine', phasePerVoice: true },
    motion: { driftAmount: 0.1, driftRateHz: 0.05, targets: ['pan', 'cutoff'], idleEvolve: true },
    space: { reverb: { size: 0.6, decaySec: 3, damping: 0.45, preDelayMs: 20, mix: 0.2 }, delay: { timeSec: 0.45, feedback: 0.55, toneHz: 3000, mix: 0.35, pingPong: true }, shimmer: { mix: 0.12, interval: 12, feedback: 0.35 }, width: 1.3 },
    envelope: { attackSec: 0.5, releaseSec: 3, swellCurve: 'exp' },
    harmony: { autoOctave: false, autoFifth: false, ji: true },
    gesture: { velocityTo: 'vibrato', radiusTo: 'brightness', angleBendCents: 40 },
    macros: { brightness: 0.5, motion: 0.3, density: 0.4, space: 0.5, shimmer: 0.12 },
  },
  // 20 ────────────────────────────────────────────────────────────────────── Bowed Bronze (blue)
  // Freeze of the cymbal swell; the comb resonator keyed to the zone gives the noise its note, and a real gong
  // (VCSL, whose fundamental sits a hair above C#3) adds a pitched body. Radius → BP sweep maps onto brightness.
  {
    id: 'bowed-bronze', name: 'Bowed Bronze', family: 'blue',
    description: 'A cymbal swell held forever and tuned by a resonator, with a gong breathing underneath.',
    sources: [
      freeze('cymbal-swell-fiatluxx', { sizeMs: 300, density: 40, spraySec: 0.5, pitchJitterCents: 0, texture: 0.6 }, { stretch: 80 }),
      sample('gong-vcsl', { levelDb: -8 }),
    ],
    layers: { sub: OFF, fifth: OFF, octaveUp: OFF, unison: NO_UNISON },
    filter: { type: 'comb', cutoffHz: 138.59, q: 0.8, keyTrack: 1, combFeedback: 0.5, lfo: { rateHz: 0.05, depth: 0.1, shape: 'perlin' } },
    breath: { rateHz: 0.08, depth: 0.12, shape: 'perlin', phasePerVoice: true },
    motion: { driftAmount: 0.2, driftRateHz: 0.04, targets: ['cutoff', 'pan'], idleEvolve: true },
    space: { reverb: { size: 0.9, decaySec: 8, damping: 0.4, preDelayMs: 30, mix: 0.35 }, delay: NO_DELAY, shimmer: NO_SHIMMER, width: 1.3 },
    envelope: { attackSec: 1, releaseSec: 5, swellCurve: 'sCurve' },
    harmony: { autoOctave: false, autoFifth: false, ji: true },
    gesture: { velocityTo: 'density', radiusTo: 'brightness', angleBendCents: 30 },
    macros: { brightness: 0.45, motion: 0.3, density: 0.55, space: 0.5, shimmer: 0 },
  },
  // 21 ────────────────────────────────────────────────────────────────────── Dalí's Water (blue)
  // Freeze of the bowed waterphone, ±30 c grain jitter, high drift, long feedback delay. Velocity → jitter maps
  // onto vibrato (pitch wobble); radius → grain size.
  {
    id: 'dalis-water', name: "Dalí's Water", family: 'blue',
    description: 'A waterphone melting: grains that will not hold their pitch, echoes that will not stop.',
    sources: [freeze('waterphone-be-steele', { sizeMs: 250, density: 28, spraySec: 1.2, pitchJitterCents: 30, texture: 0.7 }, { stretch: 70, levelDb: 8 })],
    layers: { sub: OFF, fifth: OFF, octaveUp: OFF, unison: NO_UNISON },
    filter: { type: 'bp', cutoffHz: 900, q: 0.9, keyTrack: 0.3, combFeedback: 0.3, lfo: { rateHz: 0.07, depth: 0.4, shape: 'perlin' } },
    breath: { rateHz: 0.09, depth: 0.15, shape: 'perlin', phasePerVoice: true },
    motion: { driftAmount: 0.6, driftRateHz: 0.08, targets: ['cutoff', 'detune', 'pan'], idleEvolve: true },
    space: { reverb: { size: 0.95, decaySec: 9, damping: 0.35, preDelayMs: 40, mix: 0.4 }, delay: { timeSec: 0.6, feedback: 0.6, toneHz: 2500, mix: 0.3, pingPong: true }, shimmer: NO_SHIMMER, width: 1.4 },
    envelope: { attackSec: 0.8, releaseSec: 6, swellCurve: 'sCurve' },
    harmony: { autoOctave: false, autoFifth: false, ji: true },
    gesture: { velocityTo: 'vibrato', radiusTo: 'grainSize', angleBendCents: 50 },
    macros: { brightness: 0.45, motion: 0.65, density: 0.5, space: 0.6, shimmer: 0 },
  },
  // 22 ────────────────────────────────────────────────────────────────────── Analog Nebula (green)
  // Roland modular drone (A#3). 24 dB low-pass (two cascaded biquads in the engine's 'lp' at q 1.2), cutoff LFO
  // 0.08 Hz plus drift, two voices at ±9 c spread wide, dotted-eighth-ish delay. Resampled: a synth stays a synth.
  {
    id: 'analog-nebula', name: 'Analog Nebula', family: 'green',
    description: 'A modular synth drone with the filter breathing on its own and two oscillators drifting apart.',
    sources: [sample('analog-drone-rutgermuller', { pitchMode: 'resample' })],
    layers: { sub: { on: true, levelDb: -12 }, fifth: OFF, octaveUp: OFF, unison: { voices: 2, detuneCents: 9, spread: 1, phaseScatter: 1 } },
    filter: { type: 'lp', cutoffHz: 1800, q: 1.2, keyTrack: 0.4, combFeedback: 0.3, lfo: { rateHz: 0.08, depth: 0.5, shape: 'sine' } },
    breath: { rateHz: 0.06, depth: 0.08, shape: 'sine', phasePerVoice: true },
    motion: { driftAmount: 0.3, driftRateHz: 0.05, targets: ['cutoff'], idleEvolve: true },
    space: { reverb: { size: 0.7, decaySec: 4, damping: 0.5, preDelayMs: 20, mix: 0.25 }, delay: { timeSec: 0.375, feedback: 0.35, toneHz: 3500, mix: 0.2, pingPong: true }, shimmer: NO_SHIMMER, width: 1.4 },
    envelope: { attackSec: 0.4, releaseSec: 3, swellCurve: 'exp' },
    harmony: { autoOctave: false, autoFifth: false, ji: false },
    gesture: { velocityTo: 'none', radiusTo: 'brightness', angleBendCents: 45 },
    macros: { brightness: 0.4, motion: 0.5, density: 0.4, space: 0.4, shimmer: 0 },
  },
  // 23 ────────────────────────────────────────────────────────────────────── Escher's Staircase (white)
  // Shepard synth: six octave-spaced sines under a Gaussian envelope rising endlessly around the zone pitch.
  // Radius → rate/direction maps onto 'tilt' (the engine reads it as the Shepard rate control for this preset).
  {
    id: 'eschers-staircase', name: "Escher's Staircase", family: 'white',
    description: 'Six octaves of sine climbing forever without ever getting higher, on a plate that never dries.',
    sources: [{ kind: 'synth', engine: 'shepard', shepard: { rate: 0.15, octaves: 6 }, semitoneOffset: 0, levelDb: 0 }],
    layers: { sub: OFF, fifth: OFF, octaveUp: OFF, unison: NO_UNISON },
    filter: { type: 'none', cutoffHz: 8000, q: 0.7, keyTrack: 0, combFeedback: 0.3, lfo: NO_LFO },
    breath: { rateHz: 0.06, depth: 0.06, shape: 'perlin', phasePerVoice: true },
    motion: { driftAmount: 0.05, driftRateHz: 0.03, targets: ['pan'], idleEvolve: true },
    space: { reverb: { size: 0.4, decaySec: 4, damping: 0.3, preDelayMs: 15, mix: 0.3 }, delay: NO_DELAY, shimmer: NO_SHIMMER, width: 1 },
    envelope: { attackSec: 0.8, releaseSec: 4, swellCurve: 'sCurve' },
    harmony: { autoOctave: false, autoFifth: false, ji: true },
    gesture: { velocityTo: 'none', radiusTo: 'tilt', angleBendCents: 20 },
    macros: { brightness: 0.5, motion: 0.4, density: 0.5, space: 0.4, shimmer: 0 },
  },
  // 24 ────────────────────────────────────────────────────────────────────── Solar Wind (green)
  // Wavetable supersaw, seven voices at ±12 c spread full, sine an octave down for the floor, 3 kHz LP with a
  // slow LFO, detune drift as chorus, six-second hall: the cosmic pad.
  {
    id: 'solar-wind', name: 'Solar Wind', family: 'green',
    description: 'Seven detuned saws and a sine an octave down, drifting through a slow filter in a six-second hall.',
    sources: [
      { kind: 'synth', engine: 'wavetable', wavetable: 'supersaw', semitoneOffset: 0, levelDb: 0 },
      { kind: 'synth', engine: 'wavetable', wavetable: 'sine', semitoneOffset: -12, levelDb: -9 },
    ],
    layers: { sub: OFF, fifth: OFF, octaveUp: OFF, unison: { voices: 7, detuneCents: 12, spread: 1, phaseScatter: 1 } },
    filter: { type: 'lp', cutoffHz: 3000, q: 0.8, keyTrack: 0.3, combFeedback: 0.3, lfo: { rateHz: 0.12, depth: 0.4, shape: 'sine' } },
    breath: { rateHz: 0.08, depth: 0.08, shape: 'sine', phasePerVoice: true },
    motion: { driftAmount: 0.25, driftRateHz: 0.06, targets: ['detune', 'pan'], idleEvolve: true },
    space: { reverb: { size: 0.85, decaySec: 6, damping: 0.4, preDelayMs: 30, mix: 0.35 }, delay: NO_DELAY, shimmer: NO_SHIMMER, width: 1.6 },
    envelope: { attackSec: 0.6, releaseSec: 4, swellCurve: 'sCurve' },
    harmony: { autoOctave: false, autoFifth: false, ji: true },
    gesture: { velocityTo: 'vibrato', radiusTo: 'brightness', angleBendCents: 40 },
    macros: { brightness: 0.5, motion: 0.4, density: 0.7, space: 0.5, shimmer: 0 },
  },
  // 25 ────────────────────────────────────────────────────────────────────── Breath of the World (blue)
  // Wind + ocean (staggered loops: evolving textures) + the synthetic wind for a hint of whistle; the comb
  // resonator at fb 0.85 is the bottle you blow across, keyed to the zone. Velocity → resonator feedback maps
  // onto 'buzz'; the HP 100 Hz floor lives on the brightness macro's low end. Unpitched material is stretched,
  // never resampled, so the ocean stays an ocean in every octave.
  {
    id: 'breath-of-the-world', name: 'Breath of the World', family: 'blue',
    description: 'Wind and sea blown across the mouth of a bottle that is tuned to wherever you touch.',
    sources: [
      sample('soft-wind-cribbler', { loop: { start: 0, end: 0, mode: 'stagger' }, pitchMode: 'stretch', formantLock: true }),
      sample('ocean-nox-sound', { loop: { start: 0, end: 0, mode: 'stagger' }, pitchMode: 'stretch', formantLock: true, levelDb: -4 }),
      sample('wind-synth', { pitchMode: 'stretch', formantLock: true, levelDb: -9 }),
    ],
    layers: { sub: OFF, fifth: OFF, octaveUp: OFF, unison: NO_UNISON },
    filter: { type: 'comb', cutoffHz: 138.59, q: 0.8, keyTrack: 1, combFeedback: 0.85, lfo: { rateHz: 0.05, depth: 0.1, shape: 'perlin' } },
    breath: { rateHz: 0.1, depth: 0.2, shape: 'perlin', phasePerVoice: false },
    motion: { driftAmount: 0.3, driftRateHz: 0.05, targets: ['cutoff', 'pan'], idleEvolve: true },
    space: { reverb: { size: 0.8, decaySec: 5, damping: 0.5, preDelayMs: 25, mix: 0.3 }, delay: NO_DELAY, shimmer: NO_SHIMMER, width: 1.4 },
    envelope: { attackSec: 1, releaseSec: 4, swellCurve: 'sCurve' },
    harmony: { autoOctave: false, autoFifth: false, ji: true },
    gesture: { velocityTo: 'buzz', radiusTo: 'brightness', angleBendCents: 30 },
    macros: { brightness: 0.4, motion: 0.45, density: 0.5, space: 0.45, shimmer: 0 },
  },
];

export const PRESETS: Drone[] = inputs.map((d) => parseDrone(d));
